import { createElement } from "react";
import { MANAGED_CONFIG_VENDOR_CLAUDE_CODE, MANAGED_CONFIG_VENDOR_CODEX, MANAGED_CONFIG_VENDOR_GITHUB_COPILOT } from "../../lib/managedConfigClient.js";
import { POLICY_CATEGORIES } from "./claudeCode/copy.js";
import { cloneControls, composeManagedAgentConfig, controlsFromDocument } from "./claudeCode/document.js";
import { deriveEditorValues, incompleteLists } from "./claudeCode/editorValues.js";
import { Authentication, MinimumVersion, ModelAvailability, ProvidersBackend, StartingBehavior } from "./claudeCode/SessionSetup.jsx";
import { ManagedPermissionRules } from "./claudeCode/ToolPermissions.jsx";
import { AutoModeEnvironment, ClassifierDecisions, PermitAutoMode, ReviewEveryShellCommand } from "./claudeCode/AutoMode.jsx";
import { BrowserControls, CrossMachineMessaging, PublishedOutput, RemoteDevSsh, RemoteManagement } from "./claudeCode/ExternalRemote.jsx";
import { AdminMessages } from "./claudeCode/Messaging.jsx";
import {
    ExecutionRestrictions,
    ManagedHookPolicy,
    MarketplacePolicy,
    McpServerPolicy,
    SkillSources,
} from "./claudeCode/ContentResources.jsx";
import { CodexFeatureGroup, CodexSection } from "./codex/controls.jsx";
import { FEATURE_GROUPS } from "./codex/help.js";
import { cloneCodexControls, codexControlsFromDocument, composeCodexDocument, codexDerivedValues, codexIncompleteLists } from "./codex/document.js";
import { CopilotSection } from "./copilot/controls.jsx";
import { cloneCopilotControls, composeCopilotDocument, copilotControlsFromDocument, copilotDerivedValues, copilotIncompleteLists } from "./copilot/document.js";

function categoryLabel(id) {
    return POLICY_CATEGORIES.find((category) => category.id === id).label;
}

const autoModeOn = (controls) => controls.autoModeAllowed;

/// One supported coding agent. The page reads every category, subsection,
/// control, and document function from this entry. `pointers` lists the JSON
/// pointer prefixes a subsection writes; a change is counted in the first
/// subsection whose prefix covers it.
export const CLAUDE_CODE_AGENT = Object.freeze({
    id: "claude-code",
    vendor: MANAGED_CONFIG_VENDOR_CLAUDE_CODE,
    label: "Claude Code",
    policyFile: "managed-settings.json",
    // `agent_provider` of this agent's rows in endpoint config state.
    configProvider: "anthropic",
    // Settings layer of the policy file in endpoint config state.
    observedLayer: "managedfile",
    // A higher-priority source replaces the whole file.
    shadowScope: "file",
    documentNote: null,
    appliedTo: null,
    emptyControls: () => cloneControls(),
    clone: cloneControls,
    fromDocument: controlsFromDocument,
    compose: composeManagedAgentConfig,
    incompleteLists,
    derive: deriveEditorValues,
    // Arrays whose members belong to different subsections.
    memberPointers: ["/strictPluginOnlyCustomization"],
    // Accepted settings with no control. A loaded value is written back
    // unchanged; the tally counts a change to one of them separately.
    preservedPointers: [
        "/permissions/additionalDirectories",
        "/disableClaudeAiConnectors",
        "/requiredMinimumVersion",
        "/requiredMaximumVersion",
        "/cleanupPeriodDays",
        // Accepted by the catalog, staged for upcoming category passes. Each
        // moves to its own subsection as that category is built.
        "/allowClaudeInChromeWithManagedMcp",
        "/disableDesktopLocalSessions",
        "/disableMobileSimulatorTools",
        "/disableDeepLinkRegistration",
    ],
    // Catalog containers. Their members are counted, not the container.
    containerPointers: ["/permissions", "/autoMode"],
    categories: [
        {
            id: "session",
            label: categoryLabel("session"),
            subsections: [
                { id: "authentication", pilotCheck: "Sign in with a permitted account and exercise the credential path used by automation.", label: "Authentication", pointers: ["/forceLoginMethod", "/forceLoginOrgUUID"], Component: Authentication },
                { id: "providers-backend", pilotCheck: "Start a normal session using your team's backend and credentials.", label: "Providers & backend", pointers: ["/allowedProviders", "/forceLoginGatewayUrl"], Component: ProvidersBackend },
                { id: "starting-behavior", label: "Starting behavior", pointers: ["/permissions/defaultMode", "/model", "/permissions/disableBypassPermissionsMode"], Component: StartingBehavior },
                { id: "model-availability", pilotCheck: "Start the main session and a subagent with an approved model; confirm an excluded model is refused.", label: "Model availability", pointers: ["/availableModels", "/enforceAvailableModels", "/deniedModels", "/availableModelsMatch"], Component: ModelAvailability },
                { id: "minimum-version", label: "Minimum version", pointers: ["/minimumVersion"], Component: MinimumVersion },
            ],
        },
        {
            id: "permissions",
            label: categoryLabel("permissions"),
            subsections: [
                { id: "managed-rules", pilotCheck: "Try a matching tool call and a routine nonmatching call in the permission modes your team uses.", label: "Managed permission rules", pointers: ["/permissions/deny", "/permissions/ask", "/permissions/allow", "/allowManagedPermissionRulesOnly"], Component: ManagedPermissionRules },
            ],
        },
        {
            id: "auto",
            label: categoryLabel("auto"),
            subsections: [
                { id: "permit-auto", pilotCheck: "Start a session in its usual mode and check whether an Auto-based workflow still runs.", label: "Permit auto mode", pointers: ["/permissions/disableAutoMode"], Component: PermitAutoMode },
                { id: "auto-environment", label: "Describe your environment", pointers: ["/autoMode/environment"], Component: AutoModeEnvironment, visible: autoModeOn },
                { id: "auto-decisions", pilotCheck: "Run a routine allowed task and a task the classifier should refuse.", label: "Customize classifier decisions", pointers: ["/autoMode/hard_deny", "/autoMode/soft_deny", "/autoMode/allow"], Component: ClassifierDecisions, visible: autoModeOn },
                { id: "auto-shell", pilotCheck: "Run a typical sequence of shell commands and inspect the review behavior.", label: "Review every shell command", pointers: ["/autoMode/classifyAllShell"], Component: ReviewEveryShellCommand, visible: autoModeOn },
            ],
        },
        {
            id: "inputs",
            label: categoryLabel("inputs"),
            subsections: [
                {
                    id: "mcp-servers", pilotCheck: "Load an approved integration and use one of its tools; check an excluded integration.",
                    label: "MCP server policy",
                    pointers: ["/allowedMcpServers", "/deniedMcpServers", "/allowManagedMcpServersOnly", "/allowAllClaudeAiMcps", "/managedMcpServers", { pointer: "/strictPluginOnlyCustomization", members: ["mcp"] }],
                    Component: McpServerPolicy,
                },
                { id: "marketplaces", pilotCheck: "Install a required plugin from an approved marketplace.", label: "Plugin marketplace policy", pointers: ["/strictKnownMarketplaces", "/blockedMarketplaces", "/disableCommandPluginSources", "/disableSideloadFlags"], Component: MarketplacePolicy },
                { id: "skill-sources", pilotCheck: "Run a skill or subagent used by a typical project.", label: "Skill and subagent sources", pointers: [{ pointer: "/strictPluginOnlyCustomization", exceptMembers: ["mcp"] }], Component: SkillSources },
                { id: "hooks", pilotCheck: "Run a workflow that relies on a formatting or checking hook.", label: "Hook restrictions", pointers: ["/allowManagedHooksOnly", "/allowedHttpHookUrls", "/httpHookAllowedEnvVars"], Component: ManagedHookPolicy },
                { id: "execution", pilotCheck: "Exercise a required skill or workflow that uses execution.", label: "Execution restrictions", pointers: ["/disableSkillShellExecution", "/disableWorkflows", "/disableBundledSkills"], Component: ExecutionRestrictions },
            ],
        },
        {
            id: "external",
            label: categoryLabel("external"),
            subsections: [
                { id: "remote-management", label: "Remote management", pointers: ["/disableRemoteControl"], Component: RemoteManagement },
                { id: "cross-machine", label: "Cross-machine messaging", pointers: ["/isolatePeerMachines"], Component: CrossMachineMessaging },
                { id: "remote-ssh", pilotCheck: "Connect to a permitted development host.", label: "Remote dev (SSH)", pointers: ["/sshHostAllowlist"], Component: RemoteDevSsh },
                { id: "published-output", label: "Published output", pointers: ["/disableArtifact", "/enableArtifact"], Component: PublishedOutput },
                { id: "browser", pilotCheck: "Open a local preview and test an external site in the Desktop Browser pane.", label: "Browser", pointers: ["/disableBrowserExternalNavigation", "/browserExternalPageTools"], Component: BrowserControls },
            ],
        },
        {
            id: "messaging",
            label: categoryLabel("messaging"),
            subsections: [
                { id: "admin-messages", label: "Admin messages", pointers: ["/pluginTrustMessage", "/companyAnnouncements"], Component: AdminMessages },
            ],
        },
    ],
});

function codexSection(id, label, orientation, pointers) {
    return { id, label, pointers, Component: (props) => createElement(CodexSection, { ...props, title: label, orientation, pointers }) };
}

// Each feature group owns its names' pointers in the change tally.
function featureSection(group) {
    return {
        id: `codex-features-${group.id}`,
        label: group.label,
        pointers: group.names.map((name) => `/features/${name}`),
        Component: (props) => createElement(CodexFeatureGroup, { ...props, title: group.label, orientation: group.orientation, group }),
    };
}

export const CODEX_AGENT = Object.freeze({
    id: "codex",
    vendor: MANAGED_CONFIG_VENDOR_CODEX,
    label: "Codex",
    configProvider: "openai",
    policyFile: "requirements.toml",
    observedLayer: "requirements",
    // Codex combines some requirements across sources and replaces others, so
    // the fleet row keeps the tier reason instead of naming files.
    shadowScope: null,
    documentNote: "Stored as JSON; Origin writes these settings to Codex requirements.toml on endpoints.",
    appliedTo: "requirements.toml",
    emptyControls: () => cloneCodexControls(),
    clone: cloneCodexControls,
    fromDocument: codexControlsFromDocument,
    compose: composeCodexDocument,
    incompleteLists: codexIncompleteLists,
    derive: codexDerivedValues,
    memberPointers: [],
    // Sandbox modes, read-denied paths and permission profiles (named sandbox
    // presets) are accepted by the API but have no control until sandbox
    // settings are tested against capture. A loaded value is written back
    // unchanged.
    preservedPointers: ["/allowed_sandbox_modes", "/permissions/filesystem/deny_read", "/allowed_permission_profiles", "/default_permissions"],
    containerPointers: ["/permissions", "/permissions/filesystem", "/rules", "/marketplaces", "/models", "/models/new_thread"],
    categories: [
        { id: "codex-session", label: categoryLabel("session"), subsections: [
            codexSection("codex-authentication", "Authentication", "Which sign-in methods and ChatGPT workspaces people may use.", ["/allowed_login_methods", "/allowed_chatgpt_workspaces"]),
            codexSection("codex-approval-policies", "Approval policies", "Which approval policies people may select, and the fallback.", ["/allowed_approval_policies"]),
            codexSection("codex-new-thread", "New thread defaults", "The model, reasoning effort, and service tier a new thread starts with.", ["/models/new_thread/model", "/models/new_thread/model_reasoning_effort", "/models/new_thread/service_tier"]),
            codexSection("codex-update-check", "Update check", "Whether the CLI checks for a new version at startup.", ["/check_for_update_on_startup"]),
        ] },
        { id: "codex-permissions", label: categoryLabel("permissions"), subsections: [
            codexSection("codex-command-rules", "Command rules", "Command prefixes that require approval or are blocked.", ["/rules/prefix_rules"]),
            codexSection("codex-web", "Web search", "Which web search modes people may select.", ["/allowed_web_search_modes"]),
        ] },
        { id: "codex-auto-review", label: "Auto review", subsections: [
            codexSection("codex-reviewers", "Approval reviewers", "Whether the person, the automatic reviewer, or either answers approval prompts.", ["/allowed_approvals_reviewers"]),
            codexSection("codex-reviewer-policy", "Reviewer policy", "Managed policy text for the automatic reviewer.", ["/guardian_policy_config", "/guardian_extra_policy"]),
        ] },
        { id: "codex-content", label: categoryLabel("inputs"), subsections: [
            codexSection("codex-mcp", "MCP server policy", "Which named MCP server identities people may enable.", ["/mcp_servers"]),
            codexSection("codex-marketplaces", "Plugin marketplace policy", "Which plugin marketplace sources may be added.", ["/marketplaces/restrict_to_allowed_sources", "/marketplaces/allowed_sources"]),
            codexSection("codex-hooks", "Hook restrictions", "Whether Codex blocks hooks that people, repositories, and plugins add.", ["/allow_managed_hooks_only"]),
        ] },
        { id: "codex-features", label: "Features", subsections: FEATURE_GROUPS.map(featureSection) },
        { id: "codex-external", label: categoryLabel("external"), subsections: [
            codexSection("codex-remote", "Remote management", "Whether the Codex app can be driven from another device.", ["/allow_remote_control"]),
        ] },
        { id: "codex-messaging", label: categoryLabel("messaging"), subsections: [
            codexSection("codex-instructions", "Developer instructions", "Managed instructions added to every session.", ["/additional_developer_instructions"]),
        ] },
    ],
});

function copilotSection(id, label, orientation, pointers) {
    return { id, label, pointers, Component: (props) => createElement(CopilotSection, { ...props, title: label, orientation, pointers }) };
}

export const GITHUB_COPILOT_AGENT = Object.freeze({
    id: "github-copilot",
    vendor: MANAGED_CONFIG_VENDOR_GITHUB_COPILOT,
    label: "GitHub Copilot",
    // Config-state rows for Copilot carry the microsoft provider tag.
    configProvider: "microsoft",
    policyFile: "managed-settings.json",
    observedLayer: "managedfile",
    // Copilot reads each key from its strongest source, so a native or
    // server value displaces only the keys it sets.
    shadowScope: "key",
    documentNote: "Origin writes these settings to GitHubCopilot/managed-settings.json on endpoints, beside its own telemetry block. Copilot CLI and VS Code read that file.",
    appliedTo: "GitHubCopilot/managed-settings.json",
    emptyControls: () => cloneCopilotControls(),
    clone: cloneCopilotControls,
    fromDocument: copilotControlsFromDocument,
    compose: composeCopilotDocument,
    incompleteLists: copilotIncompleteLists,
    derive: copilotDerivedValues,
    // Arrays whose members belong to different subsections.
    memberPointers: ["/strictPluginOnlyCustomization"],
    // The sandbox keys are accepted by the API but have no control yet: the
    // network keys can reach the loopback collector Copilot exports to, and
    // none has been tested against capture. A loaded value is written back
    // unchanged.
    preservedPointers: ["/sandbox"],
    containerPointers: ["/permissions"],
    categories: [
        { id: "copilot-session", label: categoryLabel("session"), subsections: [
            copilotSection("copilot-authentication", "Authentication", "Which GitHub organizations an account must belong to.", ["/forceLoginOrgs"]),
            copilotSection("copilot-starting-behavior", "Starting behavior", "The model, effort, context tier, and auto tier a new conversation starts with, and whether allow-all mode is available.", ["/model", "/effortLevel", "/contextTier", "/autoTier", "/permissions/disableBypassPermissionsMode"]),
        ] },
        { id: "copilot-permissions", label: categoryLabel("permissions"), subsections: [
            copilotSection("copilot-rules", "Managed permission rules", "Operations Copilot blocks, asks about, or runs without asking.", ["/permissions/deny", "/permissions/ask", "/permissions/allow"]),
            copilotSection("copilot-domains", "Network domains", "The only domains agent network requests may reach.", ["/permissions/limitTo"]),
        ] },
        { id: "copilot-content", label: categoryLabel("inputs"), subsections: [
            copilotSection("copilot-mcp", "MCP server policy", "Which MCP servers may load, and who decides.", ["/allowedMcpServers", "/deniedMcpServers", "/allowManagedMcpServersOnly", { pointer: "/strictPluginOnlyCustomization", members: ["mcp"] }]),
            copilotSection("copilot-marketplaces", "Plugin marketplace policy", "Which plugin marketplace sources may be added.", ["/strictKnownMarketplaces"]),
            copilotSection("copilot-skill-sources", "Skill and subagent sources", "Whether skills, agents, and hooks may come only from plugins or managed settings.", [{ pointer: "/strictPluginOnlyCustomization", exceptMembers: ["mcp"] }]),
            copilotSection("copilot-hooks", "Hook restrictions", "Whether Copilot blocks hooks that people, repositories, and plugins add.", ["/allowManagedHooksOnly"]),
        ] },
        { id: "copilot-external", label: categoryLabel("external"), subsections: [
            copilotSection("copilot-remote", "Remote management", "Whether Copilot CLI sessions can be controlled from other devices.", ["/remoteControl"]),
        ] },
    ],
});

/// Supported agents, in tab order. An agent appears here only once the API
/// accepts its vendor.
export const AGENT_DEFINITIONS = Object.freeze([CLAUDE_CODE_AGENT, CODEX_AGENT, GITHUB_COPILOT_AGENT]);
